import * as ordersApi from "@/apiv2/planner/orders";
import { getStatusDefaultValues } from "@/pages/PlannerPage/Filter/filter.helpers";

import * as routerSelectors from "../../router/router.selectors";
import { handleError } from "../../utils/error";

import {
  RESET,
  SET_IS_FETCHING_STATUSES,
  SET_SELECTED_STATUSES,
  SET_STATUSES,
} from "./statusFilter.actionTypes";
import * as selectors from "./statusFilter.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setStatuses = (data) => (dispatch) => {
  dispatch({
    type: SET_STATUSES,
    payload: data,
  });
};

export const setSelectedStatuses = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_STATUSES,
    payload: data,
  });
};

export const setIsFetchingStatuses = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_STATUSES,
    payload: status,
  });
};

export const fetchStatuses = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingStatuses(true));
    const response = await ordersApi.getAllStatuses()();

    // The seven built-ins the backend's board search accepts (filter_by_order_status):
    // Draft, Submitted, Quote, Archived, Picked Up, Delivered, Installed. The list had
    // drifted to six — Submitted was missing — for as long as nothing asserted it.
    // One row per name. Every company has a real "Submitted" status (the backend's default
    // column) on top of the built-in above, and custom names are not unique. The filter is
    // name-based, so a repeated name adds nothing to a request, and two rows sharing a name
    // share a React key, which duplicated rows on screen once the list could be searched.
    const seen = new Set();
    const statuses = [
      { uid: "Draft", name: "Draft" },
      { uid: "Submitted", name: "Submitted" },
      { uid: "Quote", name: "Quote" },
      { uid: "Archived", name: "Archived" },
      { uid: "Delivered", name: "Delivered" },
      { uid: "Picked Up", name: "Picked Up" },
      { uid: "Installed", name: "Installed" },
      ...response.data,
    ].filter((s) => !seen.has(s.name) && seen.add(s.name));

    dispatch(setStatuses(statuses));

    // A fresh board opens on the default set (every status except the off-by-default ones), the
    // same set Reset to default and a None view settle on, so the three stay identical.
    dispatch(setSelectedStatuses(getStatusDefaultValues(statuses)));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingStatuses(false));
  }
};

export const setStatusFilter = (name) => (dispatch, getState) => {
  const state = getState();

  const selectedStatuses = selectors.getSelectedStatuses(state);
  const allStatuses = selectors.getStatuses(state);

  if (name === "" && selectedStatuses.length === allStatuses.length) {
    return dispatch(setSelectedStatuses([]));
  }

  if (
    name === "" &&
    (selectedStatuses.length === 0 || selectedStatuses.length < allStatuses.length + 1)
  ) {
    return dispatch(setSelectedStatuses(allStatuses.map((a) => a.name)));
  }

  if (selectedStatuses.includes(name)) {
    return dispatch(
      setSelectedStatuses(selectedStatuses.filter((statusName) => statusName !== name))
    );
  }

  return dispatch(setSelectedStatuses([...selectedStatuses, name]));
};

// Reset to default's status half: the default set, not every status, so the panel reads as
// unfiltered afterwards (getActiveGroups compares against the same getStatusDefaultValues).
export const resetStatuses = () => (dispatch, getState) => {
  const statuses = selectors.getStatuses(getState());

  dispatch(setSelectedStatuses(getStatusDefaultValues(statuses)));
};

export const selectAllStatusesFromQueryParams = () => (dispatch, getState) => {
  const state = getState();
  const queryParams = routerSelectors.getQuery(state);

  if (queryParams.statuses) {
    dispatch(
      setSelectedStatuses(
        Array.isArray(queryParams.statuses) ? queryParams.statuses : [queryParams.statuses]
      )
    );
  } else {
    dispatch(setSelectedStatuses([]));
  }
};
